import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChefHat, Loader2, RefreshCw, Printer, Clock, CheckCircle, Wifi, WifiOff, Bell, X, Utensils, Truck, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '../components/ui/dialog';

const statusConfig = {
  pending: { border: 'border-t-yellow-500', badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', glow: 'shadow-yellow-500/10' },
  confirmed: { border: 'border-t-blue-500', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', glow: 'shadow-blue-500/10' },
  preparing: { border: 'border-t-orange-500', badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400', glow: 'shadow-orange-500/10' },
  ready: { border: 'border-t-emerald-500', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400', glow: 'shadow-emerald-500/10' },
  out_for_delivery: { border: 'border-t-purple-500', badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', glow: 'shadow-purple-500/10' },
};

function LiveTimer({ createdAt }) {
  const [elapsed, setElapsed] = useState('');
  const [urgent, setUrgent] = useState(false);
  useEffect(() => {
    const calc = () => {
      const diff = Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
      const mins = Math.floor(diff / 60);
      const secs = diff % 60;
      setElapsed(`${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`);
      setUrgent(mins >= 15);
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [createdAt]);
  return (
    <div className={`flex items-center gap-1.5 font-mono text-xs font-bold tabular-nums ${urgent ? 'text-red-500' : 'text-muted-foreground'}`} data-testid="live-timer">
      <Timer className={`w-3.5 h-3.5 ${urgent ? 'animate-pulse' : ''}`} />
      {elapsed}
    </div>
  );
}

export default function KitchenPage() {
  const { activeBranch, authHeaders, API } = useAuth();
  const { t } = useTranslation();
  const statusLabels = { pending: t('kitchen.statusPending'), confirmed: t('kitchen.statusConfirmed'), preparing: t('kitchen.statusPreparing'), ready: t('kitchen.statusReady'), out_for_delivery: t('kitchen.statusOutForDelivery') };
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [newOrderFlash, setNewOrderFlash] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const fetchOrders = useCallback(async () => {
    if (!activeBranch) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/kitchen/orders?branch_id=${activeBranch.id}`, authHeaders());
      setOrders(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [activeBranch, API, authHeaders]);

  const connectWebSocket = useCallback(() => {
    if (!activeBranch) return;
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '') + `/api/ws/kitchen/${activeBranch.id}`;
    
    if (wsRef.current) { wsRef.current.close(); }

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      // Send ping every 25s to keep alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 25000);
      ws._pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.event === 'new_order') {
          setOrders(prev => {
            const exists = prev.some(o => o.id === msg.data.id);
            if (exists) return prev;
            return [msg.data, ...prev];
          });
          setNewOrderFlash(msg.data.id);
          setTimeout(() => setNewOrderFlash(null), 3000);
          const isDineIn = msg.data.is_dine_in || msg.data.table_number > 0;
          playNotificationSound(isDineIn);
          if (isDineIn) {
            toast.success(
              t('kitchen.newTableOrder', { table: msg.data.table_number, name: msg.data.customer_name }),
              { icon: '🪑', duration: 6000 }
            );
          } else {
            toast.success(t('kitchen.newOrderFrom', { name: msg.data.customer_name }), { icon: '🔔' });
          }
        } else if (msg.event === 'order_updated') {
          setOrders(prev => prev.map(o => o.id === msg.data.id ? msg.data : o)
            .filter(o => !['delivered', 'cancelled', 'out_for_delivery'].includes(o.status)));
        }
      } catch {}
    };

    ws.onclose = () => {
      setWsConnected(false);
      if (ws._pingInterval) clearInterval(ws._pingInterval);
      reconnectTimer.current = setTimeout(connectWebSocket, 3000);
    };

    ws.onerror = () => { ws.close(); };
  }, [activeBranch, API]);

  const playNotificationSound = (isDineIn = false) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      if (isDineIn) {
        // Dine-in table order: louder, 3-tone chime (ding-ding-ding)
        gain.gain.setValueAtTime(0.5, ctx.currentTime);
        osc.frequency.setValueAtTime(1047, ctx.currentTime);       // C6
        osc.frequency.setValueAtTime(1319, ctx.currentTime + 0.15); // E6
        osc.frequency.setValueAtTime(1568, ctx.currentTime + 0.3);  // G6
        osc.frequency.setValueAtTime(1319, ctx.currentTime + 0.45); // E6
        osc.frequency.setValueAtTime(1568, ctx.currentTime + 0.6);  // G6
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.9);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.9);
        // Second oscillator for harmony
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.frequency.setValueAtTime(523, ctx.currentTime);
        osc2.frequency.setValueAtTime(659, ctx.currentTime + 0.15);
        osc2.frequency.setValueAtTime(784, ctx.currentTime + 0.3);
        gain2.gain.setValueAtTime(0.3, ctx.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.9);
        osc2.start(ctx.currentTime);
        osc2.stop(ctx.currentTime + 0.9);
      } else {
        // Regular delivery order: simple 2-tone beep
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
        osc.frequency.setValueAtTime(880, ctx.currentTime + 0.2);
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.5);
      }
    } catch {}
  };

  useEffect(() => {
    fetchOrders();
    connectWebSocket();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [activeBranch]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus }, authHeaders());
      toast.success(newStatus === 'delivered' ? t('kitchen.deliveredSuccess') : t('kitchen.statusUpdated'));
      if (!wsConnected) fetchOrders();
    } catch { toast.error(t('kitchen.updateFailed')); }
  };

  const printOrder = async (orderId) => {
    try {
      const res = await axios.get(`${API}/kitchen/ticket/${orderId}`, authHeaders());
      setTicketData(res.data);
    } catch { toast.error(t('kitchen.ticketFailed')); }
  };

  const getTimeSince = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('kitchen.now');
    if (mins < 60) return `${mins} ${t('kitchen.minutes')}`;
    return `${Math.floor(mins / 60)} س ${mins % 60} د`;
  };

  const nextAction = (status) => {
    const actions = {
      pending: { label: t('kitchen.confirmBtn'), next: 'confirmed' },
      confirmed: { label: t('kitchen.prepareBtn'), next: 'preparing' },
      preparing: { label: t('kitchen.readyBtn'), next: 'ready' },
      ready: { label: t('kitchen.deliveredBtn'), next: 'delivered' },
    };
    return actions[status];
  };

  return (
    <ModuleLayout moduleKey="preparation">
      <div className="space-y-6" data-testid="kitchen-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-bold text-2xl text-foreground flex items-center gap-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              <ChefHat className="w-6 h-6 text-primary" />
              {t('kitchen.title')}
            </h1>
            <p className="text-sm text-muted-foreground">{orders.length} {t('kitchen.activeOrders')}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${wsConnected ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-500'}`} data-testid="ws-status">
              {wsConnected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
              {wsConnected ? t('kitchen.live') : t('kitchen.offline')}
            </div>
            <Button variant="outline" size="sm" onClick={fetchOrders} data-testid="kitchen-refresh-btn" className="h-9 gap-2">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">{t('kitchen.noOrders')}</p>
            <p className="text-sm">{t('kitchen.ordersWillAppear')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {orders.map(order => {
              const action = nextAction(order.status);
              const isNew = newOrderFlash === order.id;
              const cfg = statusConfig[order.status] || statusConfig.pending;
              const isDineIn = order.is_dine_in || order.table_number > 0;
              return (
                <Card
                  key={order.id}
                  className={`border-t-4 ${cfg.border} bg-card border border-border/50 rounded-xl transition-all duration-300 hover:shadow-lg ${cfg.glow} ${isNew ? 'ring-2 ring-primary ring-offset-2 ring-offset-background animate-pulse' : ''}`}
                  data-testid={`kitchen-ticket-${order.id}`}
                >
                  <CardContent className="p-0">
                    {/* Card Header: Order # + Type + Timer */}
                    <div className="px-4 pt-3 pb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-extrabold text-foreground font-mono" data-testid={`order-num-${order.id}`}>
                          #{order.order_number || order.id?.slice(-4)}
                        </span>
                        {isDineIn ? (
                          <Badge className="text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/25 gap-1" data-testid={`table-badge-${order.id}`}>
                            <Utensils className="w-3 h-3" />
                            {t('kitchen.tableNum', { num: order.table_number })}
                          </Badge>
                        ) : (
                          <Badge className="text-[10px] bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/25 gap-1">
                            <Truck className="w-3 h-3" />
                            {t('kitchen.delivery')}
                          </Badge>
                        )}
                      </div>
                      <LiveTimer createdAt={order.created_at} />
                    </div>

                    {isNew && (
                      <div className="mx-4 mb-2 flex items-center gap-1.5 text-[11px] font-bold text-primary bg-primary/10 rounded-lg px-2.5 py-1.5">
                        <Bell className="w-3.5 h-3.5 animate-bounce" /> {t('kitchen.newOrderAlert')}
                      </div>
                    )}

                    {/* Status + Customer */}
                    <div className="px-4 pb-2 flex items-center justify-between">
                      <Badge className={`text-[10px] ${cfg.badge}`}>{statusLabels[order.status] || order.status}</Badge>
                      <div className="text-end">
                        <p className="text-xs font-semibold text-foreground">{order.customer_name}</p>
                        <p className="text-[10px] text-muted-foreground" dir="ltr">{order.customer_phone}</p>
                      </div>
                    </div>

                    {/* Items */}
                    <div className="mx-4 mb-3 bg-muted/40 rounded-lg p-3 space-y-1.5">
                      {order.items?.map((item, i) => (
                        <div key={i} className="flex justify-between items-center text-sm">
                          <span className="text-foreground">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-primary/10 text-primary text-[10px] font-bold mr-1.5">{item.quantity}</span>
                            {item.name_ar || item.name}
                          </span>
                        </div>
                      ))}
                      {order.notes && (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-2 pt-2 border-t border-border/50 font-medium">
                          {t('kitchen.notesLabel')} {order.notes}
                        </p>
                      )}
                    </div>

                    {/* Footer: Total + Actions */}
                    <div className="px-4 pb-3 flex items-center justify-between">
                      <span className="text-sm font-bold text-foreground">{order.total_price} {t('common.sar')}</span>
                      <div className="flex gap-1.5">
                        <Button variant="outline" size="sm" onClick={() => printOrder(order.id)}
                          data-testid={`kitchen-print-${order.id}`} className="h-8 w-8 p-0 rounded-lg">
                          <Printer className="w-3.5 h-3.5" />
                        </Button>
                        {action && (
                          <Button size="sm" onClick={() => updateStatus(order.id, action.next)}
                            data-testid={`kitchen-action-${order.id}`}
                            className="h-8 px-4 text-[11px] font-bold rounded-lg">
                            {action.label}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Thermal Ticket Dialog */}
      <Dialog open={!!ticketData} onOpenChange={() => setTicketData(null)}>
        <DialogContent className="max-w-sm p-0 overflow-hidden">
          {ticketData && (
            <div data-testid="print-ticket" style={{ fontFamily: 'monospace', background: '#fff', color: '#000', padding: 24, fontSize: 12, lineHeight: 1.8 }}>
              <div style={{ textAlign: 'center', borderBottom: '2px dashed #000', paddingBottom: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 18, fontWeight: 'bold' }}>{ticketData.branch_name}</div>
                <div style={{ fontSize: 10 }}>{ticketData.branch_phone}</div>
                <div style={{ fontSize: 20, fontWeight: 'bold', marginTop: 8 }}>#{ticketData.order_id}</div>
                <div style={{ fontSize: 10 }}>{new Date(ticketData.created_at).toLocaleString('ar-SA')}</div>
                {(ticketData.is_dine_in || ticketData.table_number > 0) && (
                  <div style={{ fontSize: 22, fontWeight: 'bold', marginTop: 8, background: '#f0f0f0', padding: '4px 12px', borderRadius: 8, display: 'inline-block' }}>
                    {t('kitchen.tableNum', { num: ticketData.table_number })}
                  </div>
                )}
              </div>
              <div style={{ borderBottom: '1px dashed #000', paddingBottom: 8, marginBottom: 8 }}>
                <div><strong>{t('kitchen.customerLabel')}</strong> {ticketData.customer_name}</div>
                <div><strong>{t('kitchen.phoneLabel')}</strong> {ticketData.customer_phone}</div>
                {ticketData.customer_address && <div><strong>{t('kitchen.addressLabel')}</strong> {ticketData.customer_address}</div>}
              </div>
              <div style={{ borderBottom: '1px dashed #000', paddingBottom: 8, marginBottom: 8 }}>
                {ticketData.items?.map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{item.quantity}x {item.name_ar || item.name}</span>
                    <span>{item.price * item.quantity} {t('common.sar')}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{t('kitchen.subtotalLabel')}</span><span>{ticketData.subtotal} {t('common.sar')}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{t('kitchen.deliveryLabel')}</span><span>{ticketData.delivery_fee} {t('common.sar')}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 'bold', borderTop: '2px dashed #000', paddingTop: 8, marginTop: 8 }}>
                <span>{t('kitchen.totalLabel')}</span><span>{ticketData.total_price} {t('common.sar')}</span>
              </div>
              {ticketData.notes && <div style={{ marginTop: 8, fontSize: 11, fontStyle: 'italic' }}>{t('kitchen.notesLabel')} {ticketData.notes}</div>}
              <div style={{ textAlign: 'center', marginTop: 16, fontSize: 10, color: '#666' }}>{t('kitchen.ticketThankYou')}</div>
            </div>
          )}
          <div className="p-3 border-t flex gap-2">
            <Button size="sm" className="flex-1 rounded-full gap-1" onClick={() => { window.print(); }} data-testid="ticket-print-btn">
              <Printer className="w-3.5 h-3.5" /> {t('kitchen.printBtn')}
            </Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => setTicketData(null)}>{t('kitchen.closeBtn')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </ModuleLayout>
  );
}
